import React, { createContext, useContext, useState, useCallback } from 'react';

export type NotificationType = 'success' | 'error' | 'warning' | 'info';

export interface Notification {
  id: string;
  type: NotificationType;
  message: string;
  description?: string;
  duration?: number; // milliseconds, 0 means no auto-dismiss
}

interface NotificationContextType {
  notifications: Notification[];
  showNotification: (notification: Omit<Notification, 'id'>) => void;
  showSuccess: (message: string, description?: string) => void;
  showError: (message: string, description?: string) => void;
  showWarning: (message: string, description?: string) => void;
  showInfo: (message: string, description?: string) => void;
  dismissNotification: (id: string) => void;
  clearAll: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: React.ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const dismissNotification = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const showNotification = useCallback(
    (notification: Omit<Notification, 'id'>) => {
      const id = `notification-${Date.now()}-${Math.random()}`;
      const duration = notification.duration ?? 3000; // Default 3 seconds

      const newNotification: Notification = {
        ...notification,
        id,
        duration,
      };

      setNotifications((prev) => [...prev, newNotification]);

      // Auto-dismiss after duration (if duration > 0)
      if (duration > 0) {
        setTimeout(() => {
          dismissNotification(id);
        }, duration);
      }
    },
    [dismissNotification]
  );

  const showSuccess = useCallback(
    (message: string, description?: string) => {
      showNotification({ type: 'success', message, description, duration: 3000 }); // Auto-dismiss after 3 seconds per requirement 10.5
    },
    [showNotification]
  );

  const showError = useCallback(
    (message: string, description?: string) => {
      showNotification({ type: 'error', message, description, duration: 0 }); // Require user action to dismiss per requirement 10.6
    },
    [showNotification]
  );

  const showWarning = useCallback(
    (message: string, description?: string) => {
      showNotification({ type: 'warning', message, description });
    },
    [showNotification]
  );

  const showInfo = useCallback(
    (message: string, description?: string) => {
      showNotification({ type: 'info', message, description });
    },
    [showNotification]
  );

  const clearAll = useCallback(() => {
    setNotifications([]);
  }, []);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        showNotification,
        showSuccess,
        showError,
        showWarning,
        showInfo,
        dismissNotification,
        clearAll,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};
